import { Injectable } from '@angular/core';
import { SignUpModel, authUser, loginModel } from '../interfaces/auth/auth.model';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { Observable, ReplaySubject, map } from 'rxjs';
import { Ability } from '@casl/ability';
import { unpackRules } from '@casl/ability/extra';
import { jwtDecode } from 'jwt-decode';
import { sidebarTogglerService } from './sidebar-toggler.service';
import { SafeStorageService } from './safe-storage.service';

type StoredAuthProfile = Omit<authUser, 'jwt'>;

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly AUTH_PROFILE_KEY = 'authUser';
  /** Separate key so profile JSON dumps never include the bearer token. */
  private readonly ACCESS_TOKEN_KEY = 'sessionAccessToken';

  private accessToken: string | null = null;
  private currentUser: authUser | null = null;
  private authBufferSub = new ReplaySubject<authUser | null>(1);
  authBufferSub$ = this.authBufferSub.asObservable();

  constructor(
    private http: HttpClient,
    private ability: Ability,
    private sidebarTogglerService: sidebarTogglerService,
    private safeStorage: SafeStorageService
  ) {
    this.restoreUserFromCache();
  }

  domain = environment.domain;

  login(model: loginModel) {
    return this.http.post(`${environment.base_url}api/auth/login`, model).pipe(
      map(({ data }: any) => {
        const user = data['user'] as authUser;
        const token = jwtDecode(user.jwt) as any;
        let packedAbilities = token['abilities'];
        if (typeof packedAbilities === 'string') {
          packedAbilities = JSON.parse(packedAbilities);
        }
        const abilities = unpackRules(packedAbilities);
        this.ability.update(abilities as any);
        user.abilities = abilities;

        this.persistSession(user);
        return user;
      })
    );
  }

  signup(model: SignUpModel) {
    return this.http.post<SignUpModel>(this.domain + 'auth/signup', model);
  }

  setCurrentUser(auth: authUser) {
    if (auth?.jwt) {
      this.accessToken = auth.jwt;
      localStorage.setItem(this.ACCESS_TOKEN_KEY, auth.jwt);
      sessionStorage.setItem(this.ACCESS_TOKEN_KEY, auth.jwt);
    }
    this.currentUser = auth;
    this.authBufferSub.next(auth);
  }

  getCurrentUser(): authUser | null {
    return this.currentUser;
  }

  getAccessToken(): string | null {
    return this.accessToken;
  }

  isAuthenticated(): boolean {
    if (!this.accessToken) {
      return false;
    }
    return !this.isTokenExpired(this.accessToken);
  }

  logout() {
    this.accessToken = null;
    this.currentUser = null;
    this.authBufferSub.next(null);
    this.clearSessionFromStorage();
    this.ability.update([]);
    this.sidebarTogglerService.setSidaBarState(false);
    sessionStorage.removeItem('team_members_draft');
  }

  hasRole(roles: string[]): Observable<boolean> {
    return new Observable(subscriber => {
      this.authBufferSub$.subscribe(user => {
        subscriber.next(user ? roles.includes(user.role) : false);
        subscriber.complete();
      });
    });
  }

  ListUsers() {
    return this.http.get(this.domain + 'auth/users');
  }

  ListPaginatedUsers(paramsObj: any = {}) {
    let params = new HttpParams();
    for (const key in paramsObj) {
      if (Object.prototype.hasOwnProperty.call(paramsObj, key)) {
        params = params.append(key, paramsObj[key]);
      }
    }
    return this.http.get(this.domain + 'auth/users', { params });
  }

  deleteUser(userId: number) {
    return this.http.delete(this.domain + `users/${userId}`);
  }

  GetUserRelations() {
    return this.http.get(this.domain + 'auth/relations');
  }

  updateUser(userId: number, model: any) {
    return this.http.put(this.domain + `auth/users/${userId}`, model);
  }

  changePassword(userId: number, model: { password: string }) {
    return this.http.patch(this.domain + `auth/users/${userId}/change-password`, model);
  }

  updateOwnProfile(model: { name: string; email: string; job_title?: string }) {
    return this.http.put(this.domain + 'user/profile', model);
  }

  changeOwnPassword(model: { current_password: string; new_password: string }) {
    return this.http.patch(this.domain + 'user/change-password', model);
  }

  refreshLocalProfile(partial: Partial<authUser>) {
    const current = this.getCurrentUser();
    if (!current) {
      return;
    }
    const updated = { ...current, ...partial };
    this.persistSession(updated);
  }

  updateUserEditPermission(userId: number, model: { can_edit_projects: boolean }) {
    return this.http.post(this.domain + `auth/users/${userId}/edit-permission`, model, {
      observe: 'response'
    });
  }

  isTokenExpired(token: string): boolean {
    try {
      const payload = jwtDecode(token) as { exp?: number };
      if (!payload?.exp) {
        return true;
      }
      return payload.exp < Math.floor(Date.now() / 1000);
    } catch {
      return true;
    }
  }

  private restoreUserFromCache() {
    try {
      const token = this.getTokenFromStorage();

      const profile = this.getProfileFromStorage();
      if (!profile || !token || typeof token !== 'string') {
        this.clearSessionFromStorage();
        this.currentUser = null;
        this.authBufferSub.next(null);
        return;
      }

      const isExpired = this.isTokenExpired(token);
      const isLocal = !environment.production;
      const payload = jwtDecode(token) as { iss?: string };
      const looksLikeProdIssuer =
        payload?.iss?.includes('136.243.112.205') || payload?.iss?.includes('strategy.ico.org.ae');

      if (isExpired || (isLocal && looksLikeProdIssuer)) {
        this.clearSessionFromStorage();
        this.currentUser = null;
        this.authBufferSub.next(null);
        return;
      }

      this.accessToken = token;
      const user: authUser = { ...(profile as authUser), jwt: token };
      this.currentUser = user;
      this.authBufferSub.next(user);
      const abilities = user.abilities ?? [];
      this.ability.update(abilities as any);
    } catch {
      this.clearSessionFromStorage();
      this.currentUser = null;
      this.authBufferSub.next(null);
    }
  }

  private persistSession(user: authUser) {
    const { jwt, ...profile } = user;
    this.accessToken = jwt;
    this.currentUser = user;
    this.safeStorage.setJson(localStorage, this.AUTH_PROFILE_KEY, profile);
    localStorage.setItem(this.ACCESS_TOKEN_KEY, jwt);
    sessionStorage.setItem(this.ACCESS_TOKEN_KEY, jwt);
    this.safeStorage.setJson(sessionStorage, this.AUTH_PROFILE_KEY, profile);
    this.authBufferSub.next(user);
  }

  private getTokenFromStorage(): string | null {
    return sessionStorage.getItem(this.ACCESS_TOKEN_KEY)
      ?? localStorage.getItem(this.ACCESS_TOKEN_KEY);
  }

  private getProfileFromStorage(): StoredAuthProfile | null {
    const fromLocal = this.safeStorage.getJson<StoredAuthProfile>(localStorage, this.AUTH_PROFILE_KEY);
    if (fromLocal) {
      return fromLocal;
    }

    const fromSession = this.safeStorage.getJson<StoredAuthProfile>(sessionStorage, this.AUTH_PROFILE_KEY);
    if (fromSession) {
      return fromSession;
    }

    const legacy = this.safeStorage.getJson<authUser>(localStorage, this.AUTH_PROFILE_KEY);
    if (legacy) {
      const { jwt, ...profile } = legacy;
      if (jwt) {
        localStorage.setItem(this.ACCESS_TOKEN_KEY, jwt);
        sessionStorage.setItem(this.ACCESS_TOKEN_KEY, jwt);
      }
      this.safeStorage.setJson(localStorage, this.AUTH_PROFILE_KEY, profile);
      this.safeStorage.setJson(sessionStorage, this.AUTH_PROFILE_KEY, profile);
      return profile;
    }

    return null;
  }

  private clearSessionFromStorage() {
    this.safeStorage.remove(sessionStorage, this.AUTH_PROFILE_KEY);
    this.safeStorage.remove(sessionStorage, this.ACCESS_TOKEN_KEY);
    sessionStorage.removeItem(this.ACCESS_TOKEN_KEY);
    this.safeStorage.remove(localStorage, this.AUTH_PROFILE_KEY);
    localStorage.removeItem(this.ACCESS_TOKEN_KEY);
  }
}
